import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@material-ui/core";
import { Add, Remove, Close } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import React, { useState } from "react";
import CSVUploadModal from "./VesselIndexCsvUpload";

const CreateIndexModel = (props) => {
  const {
    handleClose,
    open,
    portOptions,
    selectedPort,
    onChangePort,
    delayPortSearch,
    portLoading,
    onSubmit = () => {},
    indexValue,
    setIndexValue = () => {},
    formData,
    setFormData = () => {},
    subLoader,
    title,
    buttonTitle,
    isEdit,
  } = props;

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" })); // Clear error when input changes
  };

  const handleManualInputChange = (index, field, value) => {
    const newInputs = [...indexValue];
    newInputs[index][field] =
      String(field) === "indexValue" ? Number(value) : value;
    setIndexValue(newInputs);
    setErrors((prev) => ({ ...prev, indexValue: "" }));
  };

  const handleAddManualInput = () => {
    setIndexValue([...indexValue, { indexValue: 0, Date: "" }]);
  };

  const handleRemoveManualInput = (index) => {
    const newInputs = indexValue.filter((_, i) => i !== index);
    setIndexValue(newInputs);
  };

  const handlePortChange = (index, value) => {
    const newPorts = [...formData.port];
    newPorts[index] = value;
    setFormData((prev) => ({ ...prev, port: newPorts }));
    setErrors((prev) => ({ ...prev, port: "" })); // Clear error when input changes
  };

  const handlePortInput = () => {
    setFormData((prev) => ({ ...prev, port: [...prev.port, null] }));
  };

  const handleRemovePortInput = (index) => {
    const newPorts = formData.port.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, port: newPorts }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate formData fields
    if (!formData.indexName) newErrors.indexName = "Index Name is required";
    if (!formData.briefDescription)
      newErrors.briefDescription = "Brief Description is required";
    if (!formData.indexDetails)
      newErrors.indexDetails = "Index Details are required";
    if (!formData.vesselDetails)
      newErrors.vesselDetails = "Vessel Details are required";
    if (formData.port.some((port) => !port))
      newErrors.port = "All ports must be selected";

    // Validate indexValue fields
    if (indexValue.some((input) => !input.indexValue || !input.Date)) {
      newErrors.indexValue = "All index values and dates must be filled";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!isEdit) {
      if (!validateForm()) return;

      delete formData.inputType;
      const payload = {
        ...formData,
        indexValues: indexValue?.map((input) => ({
          Value: input.indexValue,
          Date: new Date(input?.Date)?.toISOString() || null,
        })),
      };
      onSubmit(payload);
    } else {
      onSubmit(formData);
    }
  };

  const handleCsv = (data) => {
    setIndexValue(data);
  };

  const ModelClose = () => {
    setFormData({
      indexName: "",
      inputType: "manual",
      access: "onlyme",
      briefDescription: "",
      indexDetails: "",
      vesselDetails: "",
      port: [{}],
    });
    setIndexValue([{ indexValue: 0, Date: "" }]);
    setErrors({});
    handleClose();
  };

  const isEmptyIndexDetails =
    indexValue?.length && indexValue?.some((i) => !i.indexValue || !i.Date);

  return (
    <Dialog
      open={open}
      onClose={ModelClose}
      className="superadmin-dialog"
      maxWidth="md"
      fullWidth
      aria-labelledby="create-market-index-title"
      PaperProps={{
        style: {
          borderRadius: "0px",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Modal Header */}
      <DialogTitle className="!py-1.5 !px-3 bg-slate-50 border-b border-slate-200 !m-0">
        <div className="flex justify-between items-center">
          <h2 id="create-market-index-title" className="text-[14px] font-medium text-slate-900">{title}</h2>
          <IconButton
            onClick={ModelClose}
            aria-label="Close dialog"
            size="small"
            className="text-slate-400 hover:text-slate-600 !p-0.5"
          >
            <Close style={{ fontSize: "16px" }} />
          </IconButton>
        </div>
      </DialogTitle>

      {/* Modal Content */}
      <DialogContent className="admin-form !py-3 !px-3 space-y-3" style={{ scrollbarWidth: "thin" }}>
        {/* Top 2-Column Row: Index Name & Index Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="market-index-name" className="block text-[11px] font-medium text-slate-800 mb-0.5">
              Index Name <span className="text-rose-500">*</span>
            </label>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              id="market-index-name"
              placeholder="e.g. XENDER100"
              disabled={isEdit}
              value={formData.indexName}
              onChange={(e) => handleInputChange("indexName", e.target.value)}
              error={!!errors.indexName}
              helperText={errors.indexName}
              InputProps={{ style: { borderRadius: "0px" } }}
              inputProps={{ style: { padding: "5px 8px", fontSize: "11px", fontWeight: "500" } }}
            />
          </div>
          <div>
            <label htmlFor="market-index-type" className="block text-[11px] font-medium text-slate-800 mb-0.5">
              Index Type
            </label>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              id="market-index-type"
              placeholder="e.g. NORMAL"
              value={formData.indexType}
              onChange={(e) => handleInputChange("indexType", e.target.value)}
              InputProps={{ style: { borderRadius: "0px" } }}
              inputProps={{ style: { padding: "5px 8px", fontSize: "11px", fontWeight: "500" } }}
            />
          </div>
        </div>

        {/* 2-Column Grid Layout: Left Column (Values & Ports) | Right Column (Descriptions & Access) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Left Column Box: Index Values & Ports */}
          <div className="border border-slate-200 rounded-none p-2.5 bg-white flex flex-col gap-2.5">
            {!isEdit && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-medium text-slate-800">
                    Input Values
                  </span>
                  <RadioGroup
                    row
                    aria-label="Index input format"
                    value={formData.inputType}
                    onChange={(e) => handleInputChange("inputType", e.target.value)}
                    className="!m-0"
                  >
                    <FormControlLabel
                      value="manual"
                      control={<Radio size="small" color="primary" className="!p-1" />}
                      label={<span className="text-[11px] font-medium text-slate-800">Manual</span>}
                      className="!mr-2 !my-0"
                    />
                    <FormControlLabel
                      value="csv"
                      control={<Radio size="small" color="primary" className="!p-1" />}
                      label={<span className="text-[11px] font-medium text-slate-800">CSV</span>}
                      className="!mr-0 !my-0"
                    />
                  </RadioGroup>
                </div>

                {formData.inputType === "csv" && (
                  <CSVUploadModal onUpload={handleCsv} />
                )}

                {formData.inputType === "manual" && (
                  <div className="space-y-1.5">
                    <div
                      className="flex flex-col gap-1.5 overflow-y-auto border border-slate-200 rounded-none p-1.5 bg-slate-50"
                      style={{ maxHeight: "110px" }}
                    >
                      {indexValue?.map((input, index) => (
                        <div key={index} className="flex gap-1.5 items-center">
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Index Value"
                            type="number"
                            value={input.indexValue}
                            onChange={(e) =>
                              handleManualInputChange(
                                index,
                                "indexValue",
                                e.target.value
                              )
                            }
                            error={!!errors.indexValue}
                            InputProps={{ style: { borderRadius: "0px" } }}
                            inputProps={{ "aria-label": `Index value ${index + 1}`, style: { padding: "4px 6px", fontSize: "11px", fontWeight: "500" } }}
                          />
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="date"
                            size="small"
                            value={input.Date}
                            onChange={(e) =>
                              handleManualInputChange(
                                index,
                                "Date",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.indexValue}
                            InputProps={{ style: { borderRadius: "0px" } }}
                            inputProps={{ "aria-label": `Date for value ${index + 1}`, style: { padding: "4px 6px", fontSize: "11px", fontWeight: "500" } }}
                          />
                          {indexValue.length > 1 && (
                            <IconButton
                              onClick={() => handleRemoveManualInput(index)}
                              aria-label={`Remove value ${index + 1}`}
                              size="small"
                              className="text-slate-400 hover:text-slate-600 !p-0.5"
                            >
                              <Remove style={{ fontSize: "16px" }} />
                            </IconButton>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button
                      startIcon={<Add style={{ fontSize: "14px" }} />}
                      onClick={handleAddManualInput}
                      variant="outlined"
                      color="primary"
                      disabled={isEmptyIndexDetails}
                      size="small"
                      className="!text-[11px] !font-medium !h-7 !py-0 !px-2 w-full !normal-case !rounded-none"
                    >
                      Add Another Value
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Ports Selection Section */}
            <div>
              <span className="block text-[11px] font-medium text-slate-800 mb-1">
                Ports
              </span>
              <div className="space-y-1.5">
                {formData.port.map((port, index) => (
                  <div key={index} className="flex gap-1.5 items-center">
                    <Autocomplete
                      size="small"
                      classes={{ option: "!text-[11px] !py-1 !min-h-[28px]", paper: "compact-popover !rounded-none" }}
                      options={portOptions || []}
                      value={port}
                      getOptionLabel={(option) => option?.portLabel || ""}
                      onChange={(e, value) => {
                        onChangePort("port", value);
                        handlePortChange(index, value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search Port"
                          inputProps={{ ...params.inputProps, "aria-label": `Port ${index + 1}` }}
                          variant="outlined"
                          size="small"
                          value={
                            selectedPort?.selectedPort ?? selectedPort?.portLabel ?? ""
                          }
                          onChange={(e) => delayPortSearch(e.target.value)}
                          error={!!errors.port}
                          InputProps={{
                            ...params.InputProps,
                            style: { padding: "2px 6px", fontSize: "11px", fontWeight: "500", borderRadius: "0px" },
                            endAdornment: (
                              <>
                                {portLoading && (
                                  <CircularProgress color="inherit" size={14} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      className="flex-1"
                    />
                    {formData.port.length > 1 && (
                      <IconButton
                        onClick={() => handleRemovePortInput(index)}
                        aria-label={`Remove port ${index + 1}`}
                        size="small"
                        className="text-slate-400 hover:text-slate-600 !p-0.5"
                      >
                        <Remove style={{ fontSize: "16px" }} />
                      </IconButton>
                    )}
                  </div>
                ))}
                <Button
                  startIcon={<Add style={{ fontSize: "14px" }} />}
                  onClick={handlePortInput}
                  variant="outlined"
                  color="primary"
                  size="small"
                  className="!text-[11px] !font-medium !h-7 !py-0 !px-2 w-full !normal-case !rounded-none"
                >
                  Add Another Port
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column Box: Description, Details & Access */}
          <div className="border border-slate-200 rounded-none p-2.5 bg-white flex flex-col justify-between gap-2">
            <div className="space-y-2">
              <span className="block text-[11px] font-medium text-slate-800 border-b border-slate-100 pb-1">
                Description & Specifications
              </span>

              <div>
                <label htmlFor="market-index-description" className="block text-[11px] font-medium text-slate-800 mb-0.5">
                  Brief Description <span className="text-rose-500">*</span>
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={1}
                  id="market-index-description"
                  placeholder="Short overview..."
                  value={formData.briefDescription}
                  onChange={(e) =>
                    handleInputChange("briefDescription", e.target.value)
                  }
                  error={!!errors.briefDescription}
                  helperText={errors.briefDescription}
                  InputProps={{ style: { borderRadius: "0px" } }}
                  inputProps={{ style: { padding: "4px 8px", fontSize: "11px", fontWeight: "500" } }}
                />
              </div>

              <div>
                <label htmlFor="market-index-details" className="block text-[11px] font-medium text-slate-800 mb-0.5">
                  Details of Indexes <span className="text-rose-500">*</span>
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  id="market-index-details"
                  placeholder="Specific index metadata..."
                  value={formData.indexDetails}
                  onChange={(e) => handleInputChange("indexDetails", e.target.value)}
                  error={!!errors.indexDetails}
                  helperText={errors.indexDetails}
                  InputProps={{ style: { borderRadius: "0px" } }}
                  inputProps={{ style: { padding: "4px 8px", fontSize: "11px", fontWeight: "500" } }}
                />
              </div>

              <div>
                <label htmlFor="market-index-vessel-details" className="block text-[11px] font-medium text-slate-800 mb-0.5">
                  Vessel Details <span className="text-rose-500">*</span>
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  id="market-index-vessel-details"
                  placeholder="Vessel parameters & specs..."
                  value={formData.vesselDetails}
                  onChange={(e) => handleInputChange("vesselDetails", e.target.value)}
                  error={!!errors.vesselDetails}
                  helperText={errors.vesselDetails}
                  InputProps={{ style: { borderRadius: "0px" } }}
                  inputProps={{ style: { padding: "4px 8px", fontSize: "11px", fontWeight: "500" } }}
                />
              </div>
            </div>

            {/* Access Option Radio */}
            <div className="border-t border-slate-100 pt-1.5 mt-1">
              <label className="block text-[11px] font-medium text-slate-800 mb-0.5">
                Access Option
              </label>
              <RadioGroup
                row
                aria-label="Index access"
                value={formData.access}
                onChange={(e) => handleInputChange("access", e.target.value)}
                className="!m-0"
              >
                <FormControlLabel
                  value="onlyme"
                  control={<Radio size="small" color="primary" className="!p-1" />}
                  label={<span className="text-[11px] font-medium text-slate-800">Only Me</span>}
                  className="!mr-3 !my-0"
                />
                <FormControlLabel
                  value="organization"
                  control={<Radio size="small" color="primary" className="!p-1" />}
                  label={<span className="text-[11px] font-medium text-slate-800">Organization</span>}
                  className="!mr-0 !my-0"
                />
              </RadioGroup>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Modal Actions Footer */}
      <DialogActions className="!py-1.5 !px-3 border-t border-slate-200 bg-slate-50">
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="outlined"
            size="small"
            onClick={ModelClose}
            className="!text-[11px] !font-medium text-slate-700 border-slate-300 hover:bg-slate-100 !h-7 !py-0 !px-3 !normal-case !rounded-none"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={subLoader}
            onClick={handleSave}
            className="!text-[11px] !font-medium !bg-[#313C4C] text-white hover:!bg-[#1e2632] !h-7 !py-0 !px-3 !normal-case !rounded-none"
          >
            {subLoader ? (
              <CircularProgress className="text-white" size={16} />
            ) : (
              buttonTitle || "CREATE"
            )}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIndexModel;
